import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { can, useSession } from '../lib/session';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { useAddVehicle, useArchiveVehicle } from '../lib/queries';
import type { Vehicle } from '../lib/types';
import { Alert, Button, Card, Field, SectionTitle } from './ui';
import { EraseButton } from './EraseButton';

const EMPTY = { make: '', model: '', year: '', color: '', licensePlate: '', vin: '' };

export function VehicleManager({
  customerId, vehicles, canEdit,
}: { customerId: string; vehicles: Vehicle[]; canEdit: boolean }) {
  const { data: session } = useSession();
  const mayOpen = can(session, 'orders.read');
  const { t } = useI18n();
  const add = useAddVehicle(customerId);
  const archive = useArchiveVehicle(customerId);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [confirmArchive, setConfirmArchive] = useState<string | null>(null);

  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const errorText = useApiError();
  const error = add.error instanceof ApiError ? add.error : null;

  return (
    <section>
      <SectionTitle
        action={canEdit ? (
          <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? t('common.cancel') : t('veh.add')}
          </Button>
        ) : undefined}
      >{t('veh.title')}</SectionTitle>

      {open && (
        <Card className="mb-2">
          <form
            className="grid gap-3 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              add.mutate({
                make: form.make.trim(),
                model: form.model.trim(),
                ...(form.year ? { year: Number(form.year) } : {}),
                ...(form.color ? { color: form.color.trim() } : {}),
                ...(form.licensePlate ? { licensePlate: form.licensePlate.trim() } : {}),
                ...(form.vin ? { vin: form.vin.trim() } : {}),
              }, {
                onSuccess: () => { setForm(EMPTY); setOpen(false); },
              });
            }}
          >
            {error && <div className="sm:col-span-2"><Alert>{errorText(error) ?? error.message}</Alert></div>}
            <Field label={t('veh.make')} required placeholder="BMW" value={form.make} onChange={set('make')} />
            <Field label={t('veh.model')} required placeholder="M5" value={form.model} onChange={set('model')} />
            <Field label={t('veh.year')} type="number" min={1900} max={2100} placeholder="2022" value={form.year} onChange={set('year')} />
            <Field label={t('veh.colour')} placeholder={t('veh.colourPlaceholder')} value={form.color} onChange={set('color')} />
            <Field label={t('veh.plate')} placeholder="XX-123-XX" value={form.licensePlate} onChange={set('licensePlate')} />
            <Field label={t('veh.vin')} placeholder={t('veh.optional')} value={form.vin} onChange={set('vin')} />
            <div className="sm:col-span-2">
              <Button
                type="submit"
                className="w-full"
                loading={add.isPending}
                disabled={!form.make.trim() || !form.model.trim()}
              >{t('veh.addButton')}</Button>
            </div>
          </form>
        </Card>
      )}

      {vehicles.length === 0 && !open && (
        <Card><p className="text-sm text-ink-dim">{t('veh.empty')}</p></Card>
      )}

      <div className="space-y-2">
        {vehicles.map((vehicle) => (
          <Card key={vehicle.id}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {mayOpen ? (
                  <Link
                    to={`/vehicles/${vehicle.id}`}
                    className="text-sm font-semibold text-ink transition hover:text-brand"
                  >
                    {vehicle.make} {vehicle.model}
                  </Link>
                ) : (
                  <span className="text-sm font-semibold text-ink">{vehicle.make} {vehicle.model}</span>
                )}
                <div className="tabular mt-0.5 text-xs text-ink-faint">
                  {[vehicle.year, vehicle.color, vehicle.licensePlate].filter(Boolean).join(' · ') || '—'}
                </div>
                {vehicle.vin && (
                  <div className="tabular mt-0.5 text-[0.65rem] text-ink-faint/70">VIN {vehicle.vin}</div>
                )}
                {mayOpen && (
                  <Link
                    to={`/vehicles/${vehicle.id}`}
                    className="mt-1.5 inline-flex min-h-9 items-center text-xs font-medium text-brand hover:underline"
                  >
                    {t('vehicle.history')} →
                  </Link>
                )}
              </div>
              {canEdit && (
                <button
                  onClick={() => setConfirmArchive(vehicle.id)}
                  aria-label={t('veh.archiveAria', { make: vehicle.make, model: vehicle.model })}
                  className="-mr-1 inline-flex min-h-11 shrink-0 items-center rounded-lg px-3 text-[0.7rem] text-ink-faint transition-colors hover:bg-danger/10 hover:text-danger"
                >{t('veh.archive')}</button>
              )}
              <EraseButton
                compact
                className="shrink-0"
                path={`vehicles/${vehicle.id}`}
                name={[vehicle.make, vehicle.model, vehicle.licensePlate].filter(Boolean).join(' · ')}
              />
            </div>

            {confirmArchive === vehicle.id && (
              <div className="mt-3 rounded-xl border border-warning/35 bg-warning/10 p-3">
                <div className="mt-2.5 flex gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    loading={archive.isPending}
                    onClick={() => archive.mutate(vehicle.id, {
                      onSuccess: () => setConfirmArchive(null),
                    })}
                  >{t('veh.archive')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmArchive(null)}>{t('veh.keep')}</Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
}
